import { PRODUCT_TYPE } from "@/constants/gaodingVip";
import { PermissionCodes } from "@/constants/permissionCodes";
import { ORGS_CLASS_MAP } from "@/pageComponents/GdMerge/shared";
import { switchOrg } from "@/services";
import reporter from "@/tracker/report/reporter";
import { getPage } from "@/tracker/shared/global";
import { OrgService } from "@/utils/gd-org";
import { Button, Modal } from "antd";
import classNames from "classnames";
import { useRouter } from "next/router";
import { useCallback, useMemo } from "react";
import { When } from "react-if";
import GdPermission from "../GdPermission";
import HBIcon from "../HBIcon";
import HBMessage from "../HBMessage";
import styles from "./index.module.less";

export const getOrgTypeText = (org) => {
  if (org?.company?.org_class === ORGS_CLASS_MAP.PERSONAL) {
    return "个人";
  }

  const productType =
    org?.product?.base_product_type || org?.product?.product_type;
  if (productType === PRODUCT_TYPE.FLAGSHIP) {
    return "企业";
  }

  return "团队";
};

export const PRODUCT_TYPE_LABEL_MAP = {
  FREE: <div className={classNames([styles.label, styles.free])}>免费版</div>,
  BASIC: <div className={classNames([styles.label, styles.basic])}>团队版</div>,
  PROFESSIONAL: (
    <div className={classNames([styles.label, styles.professional])}>
      专业版
    </div>
  ),
  FLAGSHIP: (
    <div className={classNames([styles.label, styles.flagship])}>企业版</div>
  ),
  INDIVIDUAL_FREE: (
    <div className={classNames([styles.label, styles.free])}>免费版</div>
  ),
  INDIVIDUAL: (
    <div className={classNames([styles.label, styles.individual])}>基础版</div>
  ),
  INDIVIDUAL_PROFESSIONAL_INDIVIDUAL: (
    <div className={classNames([styles.label, styles.individual])}>基础版</div>
  ),
  INDIVIDUAL_PROFESSIONAL_ENTERPRISE: (
    <div className={classNames([styles.label, styles.individual])}>基础版</div>
  ),
};

const sortOrgs = (orgs) => {
  return orgs.sort((a, b) => {
    if (a.disabled === b.disabled) {
      return 0;
    } else if (a.disabled) {
      return 1;
    } else if (b.disabled) {
      return -1;
    }

    return 0;
  });
};

export default function GdOrgList({
  className,
  dataSource,
  value,
  onChange,
  autoSwitchOnClick = false,
  showCreateButton = false,
  onOrgCreated,
}: {
  className?: string;
  dataSource: IGdOrgInfo[];
  value: IGdOrgInfo;
  onChange?: (org: IGdOrgInfo, isChanged: boolean) => void;
  autoSwitchOnClick?: boolean;
  showCreateButton?: boolean;
  onOrgCreated?: () => void;
}) {
  const orgService = OrgService.getInstance();

  const { personalOrgs, teamOrgs } = useMemo(() => {
    const personalOrgs = [];
    const teamOrgs = [];
    dataSource?.forEach((org) => {
      if (org.company.org_class === ORGS_CLASS_MAP.PERSONAL) {
        personalOrgs.push(org);
      } else {
        teamOrgs.push(org);
      }
    });

    return {
      personalOrgs: sortOrgs(personalOrgs),
      teamOrgs: sortOrgs(teamOrgs),
    };
  }, [dataSource]);

  const confirmChange = useCallback((): Promise<boolean> => {
    return new Promise((resolve) => {
      Modal.confirm({
        title: "是否切换",
        content: `切换身份后，已打开的页面权限将刷新。请确保设计内容已妥善保存。`,
        okText: "切换",
        cancelText: "取消",
        maskClosable: true,
        onOk: () => {
          // 上报确认按钮点击事件
          const page = getPage();
          reporter({
            event_id: 39008,
            event_name: "button_click",
            button_name: "切换",
            ...page,
            module_name: "企业切换确定弹窗",
            subject_name: "弹窗",
            source: "顶部导航栏",
          });
          resolve(true);
        },
        onCancel: () => {
          // 上报取消按钮点击事件
          const page = getPage();
          reporter({
            event_id: 39008,
            event_name: "button_click",
            button_name: "取消",
            ...page,
            module_name: "企业切换确定弹窗",
            subject_name: "弹窗",
            source: "顶部导航栏",
          });
          resolve(false);
        },
      });

      // 打开即上报曝光
      const page = getPage();
      reporter({
        ...page,
        event_id: 39012,
        event_name: "dialog_expose",
        module_name: "企业切换弹窗",
        subject_name: "弹窗",
        source: "顶部导航栏",
      });
    });
  }, []);

  const router = useRouter();

  const onSelect = useCallback(
    async (org) => {
      const isChanged = org?.org_id !== value?.org_id;
      onChange?.(org, isChanged);

      // 组织没变，不执行任何操作
      if (!isChanged) {
        // HBMessage.info("你已经在当前组织了");
        return;
      }

      if (!autoSwitchOnClick || (await confirmChange())) {
        const switchRes = await switchOrg(org.org_id);
        if (switchRes.err) {
          return;
        }

        HBMessage.success("切换组织成功");
        router.reload();
      }
    },
    [onChange, autoSwitchOnClick, confirmChange, value]
  );

  // 获取默认头像
  const getDefaultAvatar = useCallback((org) => {
    // 个人版
    if (org?.company?.org_class === ORGS_CLASS_MAP.PERSONAL) {
      return "https://img1.ttxsapp.com/avatar/default/2.png";
    }

    // 企业版
    const productType =
      org?.product?.base_product_type || org?.product?.product_type;
    if (productType === PRODUCT_TYPE.FLAGSHIP) {
      return "https://cdn.dancf.com/fe-assets/20250317/dcc0f2a17432896c81f36bc2aba865a8.svg";
    }

    // 团队版和其他情况
    return "https://cdn.dancf.com/fe-assets/20250317/357fe3cb7d4e26d928ebed6e9219c54c.svg"; // 这里需要替换为实际的链接B
  }, []);

  const renderOrg = useCallback(
    (org) => (
      <div
        className={classNames([
          styles.item,
          org?.disabled
            ? styles.disabled
            : value?.org_id === org?.org_id
            ? styles.active
            : null,
        ])}
        key={org?.org_id}
        onClick={() => onSelect(org)}
        data-gd-click="button_click"
        data-module-name="企业切换弹窗"
        data-subject-name="按钮"
        data-button-name="企业切换"
      >
        <div
          className={classNames([
            styles.avatar,
            org?.company?.org_class === ORGS_CLASS_MAP.PERSONAL
              ? styles.circle
              : null,
          ])}
        >
          <img
            src={org?.company?.logo?.url || getDefaultAvatar(org)}
            alt="头像"
          />
        </div>
        <div className={styles.info}>
          <div className={styles.name}>
            <div className={styles.text} title={org?.company?.name}>
              {org?.company?.name}
            </div>
            {
              PRODUCT_TYPE_LABEL_MAP[
                org?.product?.base_product_type || org?.product?.product_type
              ]
            }
          </div>
          <div className={styles.desc}>
            {[
              getOrgTypeText(org),
              org?.company?.org_class !== ORGS_CLASS_MAP.PERSONAL &&
              org?.member_count
                ? `${org?.member_count}人`
                : "",
            ]
              ?.filter(Boolean)
              .join("·")}
            {org?.isNew ? "（新建组织）" : ""}
          </div>
        </div>
        <When condition={org?.last_bind_org}>
          <div className={styles.hint}>上次使用</div>
        </When>
        <div className={styles.action}>
          <div className={styles.unchecked}></div>
          <HBIcon className={styles.checked} type="ic_check_circle" />
        </div>
      </div>
    ),
    [onSelect, value]
  );

  const canCreatePersonalOrg = useMemo(() => {
    return !dataSource?.some(
      (org) => org.company.org_class === ORGS_CLASS_MAP.PERSONAL
    );
  }, [dataSource]);

  const createOrg = useCallback(async () => {
    await orgService.createOrg();
    onOrgCreated?.();
  }, [onOrgCreated]);

  return (
    <div className={classNames(styles.list, className)}>
      {personalOrgs.map((org) => renderOrg(org))}
      <When condition={personalOrgs.length !== 0 && teamOrgs.length !== 0}>
        <div className={styles.split}></div>
      </When>
      {teamOrgs.map((org) => renderOrg(org))}

      <div className={styles.footer}>
        <When condition={showCreateButton}>
          <div className={styles.createButton}>
            <GdPermission code={PermissionCodes.createOrgEntry}>
              <Button
                block
                type="text"
                onClick={createOrg}
                data-gd-click="button_click"
                data-module-name="企业切换弹窗"
                data-subject-name="按钮"
                data-button-name="企业新建"
              >
                {canCreatePersonalOrg ? "创建个人/团队/企业" : "创建团队/企业"}
              </Button>
            </GdPermission>
          </div>
        </When>
      </div>
    </div>
  );
}
